/* ============================================================
 * Lion Theme — wishlist.js
 *
 * Client-side wishlist (localStorage). Theme-Store compliant:
 *   - No external service, no app dependency.
 *   - Stores product handles only (no PII, no account binding).
 *   - Falls back gracefully if localStorage is unavailable.
 *
 * Custom elements:
 *   <lion-wishlist-toggle data-product-handle data-product-id>
 *     Heart button. Toggles handle in/out. Reflects aria-pressed.
 *   <lion-wishlist-count>
 *     Badge that shows the current item count. Hidden when zero.
 *   <lion-wishlist-grid data-empty-target>
 *     Renders product cards from stored handles via
 *     /products/{handle}?view=card (templates/product.card.liquid).
 *
 * Cross-component sync:
 *   document dispatches `wishlist:change` (detail: { handles, action }).
 *   Other tabs sync via the `storage` event automatically.
 * ============================================================ */

(function () {
const STORAGE_KEY = 'lion:wishlist';
const MAX_ITEMS = 100;
const EVENT_NAME = 'wishlist:change';

/* ------------------------------------------------------------
 * Storage helpers
 * ------------------------------------------------------------ */
function readHandles() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

function writeHandles(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    return true;
  } catch {
    return false;
  }
}

function emitChange(action, handle) {
  document.dispatchEvent(new CustomEvent(EVENT_NAME, {
    detail: { handles: readHandles(), action, handle }
  }));
}

/* ------------------------------------------------------------
 * Public API on window.lionWishlist
 * ------------------------------------------------------------ */
const wishlist = {
  has(handle) {
    return readHandles().includes(handle);
  },
  add(handle) {
    if (!handle) return false;
    const list = readHandles().filter((h) => h !== handle);
    list.unshift(handle);
    if (writeHandles(list)) {
      emitChange('add', handle);
      return true;
    }
    return false;
  },
  remove(handle) {
    if (!handle) return false;
    const list = readHandles().filter((h) => h !== handle);
    if (writeHandles(list)) {
      emitChange('remove', handle);
      return true;
    }
    return false;
  },
  toggle(handle) {
    return this.has(handle) ? (this.remove(handle), false) : (this.add(handle), true);
  },
  clear() {
    if (writeHandles([])) {
      emitChange('clear', null);
      return true;
    }
    return false;
  },
  count() {
    return readHandles().length;
  },
  list() {
    return readHandles();
  }
};

window.lionWishlist = wishlist;

/* Cross-tab sync via storage event */
window.addEventListener('storage', (e) => {
  if (e.key === STORAGE_KEY) emitChange('sync', null);
});

/* ------------------------------------------------------------
 * <lion-wishlist-toggle> — heart button on cards and PDP
 * ------------------------------------------------------------ */
class WishlistToggle extends HTMLElement {
  constructor() {
    super();
    this.handle = this.dataset.productHandle || '';
    this.button = this.querySelector('button');
    if (!this.handle || !this.button) return;

    this.button.addEventListener('click', this.onClick.bind(this));
    this.syncState();

    this.changeUnsub = (e) => {
      if (!e.detail) return;
      // Re-sync on any change (other instances on the page or other tabs)
      this.syncState();
    };
    document.addEventListener(EVENT_NAME, this.changeUnsub);
  }

  disconnectedCallback() {
    if (this.changeUnsub) document.removeEventListener(EVENT_NAME, this.changeUnsub);
  }

  onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const isActive = wishlist.toggle(this.handle);
    this.syncState();
    this.announce(isActive);
    this.pulse();
  }

  syncState() {
    const active = wishlist.has(this.handle);
    this.button.setAttribute('aria-pressed', String(active));
    this.classList.toggle('is-active', active);
    const labelOn = this.dataset.labelOn || 'Remove from wishlist';
    const labelOff = this.dataset.labelOff || 'Add to wishlist';
    this.button.setAttribute('aria-label', active ? labelOn : labelOff);
  }

  announce(isActive) {
    const region = document.getElementById('wishlist-live-region');
    if (!region) return;
    region.textContent = isActive
      ? (this.dataset.announceAdded || 'Added to wishlist')
      : (this.dataset.announceRemoved || 'Removed from wishlist');
  }

  pulse() {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    this.button.setAttribute('data-pulse', '');
    setTimeout(() => this.button.removeAttribute('data-pulse'), 420);
  }
}

if (!customElements.get('lion-wishlist-toggle')) {
  customElements.define('lion-wishlist-toggle', WishlistToggle);
}

/* ------------------------------------------------------------
 * <lion-wishlist-count> — header badge
 * ------------------------------------------------------------ */
class WishlistCount extends HTMLElement {
  constructor() {
    super();
    this.update = this.update.bind(this);
    this.update();
    document.addEventListener(EVENT_NAME, this.update);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT_NAME, this.update);
  }

  update() {
    const n = wishlist.count();
    this.textContent = n;
    this.toggleAttribute('hidden', n === 0);
  }
}

if (!customElements.get('lion-wishlist-count')) {
  customElements.define('lion-wishlist-count', WishlistCount);
}

/* ------------------------------------------------------------
 * <lion-wishlist-grid> — renders cards on the wishlist page
 * Markup expected:
 *   <lion-wishlist-grid>
 *     <ul data-wishlist-grid-target></ul>
 *     <div data-wishlist-empty hidden>...</div>
 *     <button data-wishlist-clear hidden>Clear all</button>
 *   </lion-wishlist-grid>
 * ------------------------------------------------------------ */
class WishlistGrid extends HTMLElement {
  constructor() {
    super();
    this.target = this.querySelector('[data-wishlist-grid-target]');
    this.emptyEl = this.querySelector('[data-wishlist-empty]');
    this.clearBtn = this.querySelector('[data-wishlist-clear]');

    this.onChange = window.debounce?.(this.render.bind(this), 120) || this.render.bind(this);
    document.addEventListener(EVENT_NAME, this.onChange);

    this.clearBtn?.addEventListener('click', () => {
      if (confirm(this.dataset.clearConfirm || 'Clear your wishlist?')) {
        wishlist.clear();
      }
    });

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT_NAME, this.onChange);
  }

  async render() {
    const handles = wishlist.list();

    if (!this.target) return;

    if (!handles.length) {
      this.target.innerHTML = '';
      this.emptyEl?.removeAttribute('hidden');
      this.clearBtn?.setAttribute('hidden', '');
      return;
    }

    this.emptyEl?.setAttribute('hidden', '');
    this.clearBtn?.removeAttribute('hidden');
    this.classList.add('is-loading');

    const results = await Promise.all(handles.map((h) => this.fetchCard(h)));
    const html = results
      .map((card, i) => card ? `<li class="wishlist__item" data-handle="${handles[i]}">${card}</li>` : '')
      .filter(Boolean)
      .join('');

    this.target.innerHTML = html;
    this.classList.remove('is-loading');

    // Prune handles that 404'd (product was deleted/unpublished)
    const validHandles = handles.filter((h, i) => results[i]);
    if (validHandles.length !== handles.length) writeHandles(validHandles);
  }

  async fetchCard(handle) {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?view=card`);
      if (!res.ok) return null;
      const text = await res.text();
      return text.trim();
    } catch {
      return null;
    }
  }
}

if (!customElements.get('lion-wishlist-grid')) {
  customElements.define('lion-wishlist-grid', WishlistGrid);
}

/* ------------------------------------------------------------
 * <lion-wishlist-drawer> — slide-in panel triggered by the header
 * heart icon when `wishlist_mode = drawer`. Reuses the same
 * product-card fetch as <lion-wishlist-grid> so the merchant can
 * switch between drawer and dedicated page without extra setup.
 * ------------------------------------------------------------ */
class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.target = this.querySelector('[data-wishlist-drawer-target]');
    this.emptyEl = this.querySelector('[data-wishlist-drawer-empty]');
    this.footerEl = this.querySelector('[data-wishlist-drawer-footer]');
    this.clearBtn = this.querySelector('[data-wishlist-drawer-clear]');
    this.isOpen = false;
    this.previouslyFocused = null;
    this.rendered = false;

    this.boundChange = window.debounce?.(this.render.bind(this), 120) || this.render.bind(this);
    document.addEventListener(EVENT_NAME, this.boundChange);

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-wishlist-drawer-open]');
      if (opener) {
        e.preventDefault();
        this.open(opener);
      }
    });

    this.querySelectorAll('[data-wishlist-drawer-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.clearBtn?.addEventListener('click', () => {
      if (confirm(this.dataset.clearConfirm || 'Clear your wishlist?')) {
        wishlist.clear();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT_NAME, this.boundChange);
  }

  open(triggerEl) {
    if (this.isOpen) return;
    this.previouslyFocused = triggerEl || document.activeElement;
    this.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => this.setAttribute('open', ''));
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(this, this.querySelector('[data-wishlist-drawer-close]'));
    }
    this.isOpen = true;
    if (!this.rendered) this.render();
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(this.previouslyFocused);
    }
    setTimeout(() => this.setAttribute('hidden', ''), 320);
    this.isOpen = false;
  }

  async render() {
    const handles = wishlist.list();
    if (!this.target) return;

    if (!handles.length) {
      this.target.replaceChildren();
      this.emptyEl?.removeAttribute('hidden');
      this.footerEl?.setAttribute('hidden', '');
      this.rendered = true;
      return;
    }

    this.emptyEl?.setAttribute('hidden', '');
    this.footerEl?.removeAttribute('hidden');
    this.classList.add('is-loading');

    const cards = await Promise.all(handles.map((h) => this._fetchCard(h)));
    const valid = handles.filter((_, i) => cards[i]);

    // Build via DOMParser so each card snippet returned by Shopify is parsed
    // as HTML once and appended as live nodes — no innerHTML on untrusted text.
    const parser = new DOMParser();
    this.target.replaceChildren();
    cards.forEach((card, i) => {
      if (!card) return;
      const doc = parser.parseFromString(`<ul>${card}</ul>`, 'text/html');
      const cardEl = doc.querySelector('ul').firstElementChild || doc.querySelector('ul');
      const li = document.createElement('li');
      li.className = 'wishlist-drawer__item';
      li.dataset.handle = handles[i];
      if (cardEl) li.appendChild(cardEl);
      this.target.appendChild(li);
    });

    if (valid.length !== handles.length) writeHandles(valid);
    this.classList.remove('is-loading');
    this.rendered = true;
  }

  async _fetchCard(handle) {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?view=card`);
      if (!res.ok) return null;
      const text = await res.text();
      return text.trim();
    } catch {
      return null;
    }
  }
}

if (!customElements.get('lion-wishlist-drawer')) {
  customElements.define('lion-wishlist-drawer', WishlistDrawer);
}
})();
